/**
 * LaunchDarkly debugging utilities
 * This file exposes debugging functions to the window object
 */

// Function to find the LaunchDarkly client in the React component tree
const findLDClient = () => {
  if (typeof window === 'undefined') return null;

  // Check if client is already exposed
  if (window.ldClient) {
    return window.ldClient;
  }

  // Try to find the client in the React component tree
  try {
    // Look for the LaunchDarkly context in the React component tree
    const ldElements = document.querySelectorAll('[data-reactroot]');

    // Log for debugging
    console.log('Searching for LaunchDarkly client in React component tree...');

    // If we can't find it through React, try to access it through the window object
    if (window.LD_WRAPPER && window.LD_WRAPPER.client) {
      console.log('Found LaunchDarkly client in window.LD_WRAPPER');
      return window.LD_WRAPPER.client;
    }

    // If we still can't find it, create a dummy client for debugging
    console.warn('Could not find LaunchDarkly client. Creating a dummy client for debugging.');
    return {
      variation: (key, defaultValue) => {
        console.warn(`Using dummy client. Flag '${key}' returning default value:`, defaultValue);
        return defaultValue;
      },
      allFlags: () => {
        console.warn('Using dummy client. No flags available.');
        return {};
      }
    };
  } catch (error) {
    console.error('Error finding LaunchDarkly client:', error);
    return null;
  }
};

// Initialize debugging utilities
const initLDDebugTools = () => {
  if (typeof window !== 'undefined') {
    // Create a wrapper to hold the client
    window.LD_WRAPPER = window.LD_WRAPPER || {};

    // Helper function to get the LaunchDarkly client
    window.getLDClient = () => {
      // Try to get the client from the window object
      const client = window.ldClient || findLDClient();

      if (client) {
        // Store the client in our wrapper
        window.LD_WRAPPER.client = client;
        window.ldClient = client;
        console.log('LaunchDarkly client found:', client);
        return client;
      } else {
        console.error('LaunchDarkly client not found. Make sure the app is fully loaded.');
        return null;
      }
    };

    // Helper function to evaluate a flag manually
    window.evaluateFlag = (flagKey, defaultValue = false) => {
      const client = window.getLDClient();
      if (client) {
        try {
          const value = client.variation(flagKey, defaultValue);
          console.log(`Flag '${flagKey}' value:`, value);
          return value;
        } catch (error) {
          console.error(`Error evaluating flag '${flagKey}':`, error);
          return defaultValue;
        }
      }
      return defaultValue;
    };

    // Helper function to list all available flags
    window.listAllFlags = () => {
      const client = window.getLDClient();
      if (client) {
        try {
          const allFlags = client.allFlags();
          console.log('All available flags:', allFlags);
          return allFlags;
        } catch (error) {
          console.error('Error getting all flags:', error);
          return {};
        }
      }
      return {};
    };

    // Helper function to check the current user context
    window.checkLDContext = () => {
      const client = window.getLDClient();
      if (client) {
        try {
          // Get the current user context
          const context = client.getContext();
          console.log('Current LaunchDarkly context:', context);

          // Check if domain is included
          if (context && context.custom && context.custom.domain) {
            console.log('✅ Domain is included in context:', context.custom.domain);
          } else {
            console.warn('❌ Domain is NOT included in context');
          }

          return context;
        } catch (error) {
          console.error('Error getting LaunchDarkly context:', error);
          return null;
        }
      }
      return null;
    };

    // Set up polling to find the client if it's not immediately available
    if (!window.ldClient) {
      const maxAttempts = 10;
      let attempts = 0;

      const findClientInterval = setInterval(() => {
        attempts++;
        console.log(`Attempt ${attempts} to find LaunchDarkly client...`);

        const client = findLDClient();
        if (client) {
          window.ldClient = client;
          window.LD_WRAPPER.client = client;
          console.log('LaunchDarkly client found and exposed as window.ldClient');
          clearInterval(findClientInterval);
        } else if (attempts >= maxAttempts) {
          console.warn(`Failed to find LaunchDarkly client after ${maxAttempts} attempts`);
          clearInterval(findClientInterval);
        }
      }, 1000); // Check every second
    }

    console.log('LaunchDarkly debug utilities added to window object: getLDClient(), evaluateFlag(), listAllFlags(), checkLDContext()');
  }
};

// Export the initialization function
export default initLDDebugTools;
